import { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/use-auth"
import { useCurrentOrg, orgPath } from "@/hooks/use-current-org"
import { helpSectionAnchor } from "@/lib/help-anchors"
import { omniSearch, type SearchResult, type FaqResult, type SearchResponse } from "@/services/search"
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command"
import { Badge } from "@/components/ui/badge"
import {
  Loader2,
  HelpCircle,
  BookOpen,
  Calendar,
  ClipboardList,
  Building2,
  UserCircle,
  FileText,
  CreditCard,
  FileSignature,
  Users,
  GraduationCap,
  AlertTriangle,
  FolderOpen,
  Clock,
} from "lucide-react"

const RECENTS_KEY = "omni-search-recents"
const MAX_RECENTS = 8

interface RecentItem {
  entity_type: string
  entity_id: string
  title: string
  subtitle: string | null
  url: string
  tenant_slug: string
  module_color: string
}

const moduleColors: Record<string, string> = {
  violet: "text-violet-500",
  blue: "text-blue-500",
  amber: "text-amber-500",
  rose: "text-rose-500",
  emerald: "text-emerald-500",
  orange: "text-orange-500",
  sky: "text-sky-500",
  indigo: "text-indigo-500",
  slate: "text-slate-500",
  teal: "text-teal-500",
}

const entityIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  program: BookOpen,
  session: Calendar,
  registration: ClipboardList,
  account: Building2,
  contact: UserCircle,
  quote: FileText,
  invoice: CreditCard,
  contract: FileSignature,
  staff: Users,
  instructor: GraduationCap,
  complaint: AlertTriangle,
  document: FolderOpen,
}

function getRecents(): RecentItem[] {
  const raw = localStorage.getItem(RECENTS_KEY)
  if (!raw) return []
  return JSON.parse(raw) as RecentItem[]
}

function addRecent(item: RecentItem): void {
  const recents = getRecents().filter(r => r.entity_id !== item.entity_id)
  recents.unshift(item)
  localStorage.setItem(RECENTS_KEY, JSON.stringify(recents.slice(0, MAX_RECENTS)))
}

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { availableTenants, switchTenant } = useAuth()
  const orgSlug = useCurrentOrg()

  const [query, setQuery] = useState("")
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<SearchResponse | null>(null)
  const [recents, setRecents] = useState<RecentItem[]>([])
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load recents when dialog opens
  useEffect(() => {
    if (open) {
      setRecents(getRecents())
      setQuery("")
      setResponse(null)
    }
  }, [open])

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!query.trim()) {
      setResponse(null)
      setLoading(false)
      return
    }

    setLoading(true)
    // Les réponses arrivent dans le désordre.
    // Seule celle de la dernière saisie compte.
    const superseded = { value: false }
    debounceRef.current = setTimeout(() => {
      omniSearch(query.trim())
        .then((data) => {
          if (superseded.value) return
          setResponse(data)
          setLoading(false)
        })
        .catch(() => {
          if (superseded.value) return
          setLoading(false)
        })
    }, 300)

    return () => {
      superseded.value = true
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query])

  const handleSelect = useCallback(
    (result: SearchResult | RecentItem) => {
      const recent: RecentItem = {
        entity_type: result.entity_type,
        entity_id: result.entity_id,
        title: result.title,
        subtitle: result.subtitle,
        url: result.url,
        tenant_slug: result.tenant_slug,
        module_color: result.module_color,
      }
      addRecent(recent)
      onOpenChange(false)

      if (result.tenant_slug !== orgSlug) {
        const targetTenant = availableTenants.find(t => t.slug === result.tenant_slug)
        if (targetTenant) {
          switchTenant(targetTenant).then(() => {
            navigate(orgPath(result.tenant_slug, result.url))
          })
          return
        }
      }

      navigate(orgPath(orgSlug, result.url))
    },
    [orgSlug, availableTenants, switchTenant, navigate, onOpenChange]
  )

  const handleFaqSelect = useCallback(
    (faq: FaqResult) => {
      onOpenChange(false)
      // La page d'aide défile elle-même vers l'ancre de la section.
      navigate(orgPath(orgSlug, `/help#${helpSectionAnchor(faq.section)}`))
    },
    [orgSlug, navigate, onOpenChange]
  )

  // Group results by entity_type
  const groupedResults: Record<string, SearchResult[]> = {}
  if (response) {
    for (const result of response.results) {
      const group = groupedResults[result.entity_type] || []
      group.push(result)
      groupedResults[result.entity_type] = group
    }
  }

  const entityTypeLabels: Record<string, string> = {
    program: "Programmes",
    session: "Sessions",
    registration: "Inscriptions",
    account: "Comptes",
    contact: "Contacts",
    quote: "Devis",
    invoice: "Factures",
    contract: "Contrats",
    staff: "Personnel",
    instructor: "Formateurs",
    complaint: "Réclamations",
    document: "Documents",
  }

  const hasQuery = query.trim().length > 0
  const hasFaq = response && response.faq.length > 0
  const hasResults = response && response.results.length > 0
  const hasRecents = recents.length > 0

  return (
    // Le serveur filtre déjà.
    // cmdk n'écarte pas un résultat trouvé hors du titre.
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false} label="Rechercher partout">
      <CommandInput
        placeholder="Rechercher partout..."
        value={query}
        onValueChange={setQuery}
        // Le serveur refuse au-delà de 100 caractères (422 SEARCH_TOO_LONG).
        maxLength={100}
        data-test="global-search-input"
      />
      <CommandList className="max-h-[400px]">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Empty state after search */}
        {!loading && hasQuery && !hasFaq && !hasResults && (
          <CommandEmpty>Aucun résultat pour "{query}"</CommandEmpty>
        )}

        {/* Recents (before typing) */}
        {!hasQuery && !loading && hasRecents && (
          <CommandGroup heading="Récents">
            {recents.map((item) => {
              const Icon = entityIcons[item.entity_type] || FileText
              const colorClass = moduleColors[item.module_color] || "text-muted-foreground"
              return (
                <CommandItem
                  key={`recent-${item.entity_id}`}
                  value={`recent-${item.entity_id}`}
                  onSelect={() => handleSelect(item)}
                  className="cursor-pointer"
                >
                  <Clock className="mr-2 h-4 w-4 text-muted-foreground/60" />
                  <Icon className={`mr-2 h-4 w-4 ${colorClass}`} />
                  <div className="flex flex-1 items-center gap-2 overflow-hidden">
                    <span className="truncate font-medium">{item.title}</span>
                    {item.subtitle && (
                      <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>
                    )}
                  </div>
                  {item.tenant_slug !== orgSlug && (
                    <Badge variant="outline" className="ml-2 shrink-0 text-[10px]">
                      {item.tenant_slug}
                    </Badge>
                  )}
                </CommandItem>
              )
            })}
          </CommandGroup>
        )}

        {/* No recents, no query */}
        {!hasQuery && !loading && !hasRecents && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Tapez pour rechercher dans toutes vos organisations
          </div>
        )}

        {/* FAQ results */}
        {!loading && hasFaq && (
          <>
            <CommandGroup heading="Aide">
              {response!.faq.map((faq, i) => (
                <CommandItem
                  key={`faq-${i}`}
                  value={`faq-${i}-${faq.question}`}
                  onSelect={() => handleFaqSelect(faq)}
                  className="cursor-pointer"
                >
                  <HelpCircle className="mr-2 h-4 w-4 text-teal-500" />
                  <div className="flex flex-col overflow-hidden">
                    <span className="truncate font-medium">{faq.question}</span>
                    <span className="truncate text-xs text-muted-foreground">{faq.answer}</span>
                  </div>
                  <Badge variant="outline" className="ml-auto shrink-0 text-[10px]">
                    {faq.section}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
            {hasResults && <CommandSeparator />}
          </>
        )}

        {/* Entity results grouped by type */}
        {!loading &&
          Object.entries(groupedResults).map(([entityType, results]) => {
            const Icon = entityIcons[entityType] || FileText
            const label = entityTypeLabels[entityType] || entityType
            return (
              <CommandGroup key={entityType} heading={label}>
                {results.map((result) => {
                  const colorClass = moduleColors[result.module_color] || "text-muted-foreground"
                  return (
                    <CommandItem
                      key={`${result.entity_type}-${result.entity_id}`}
                      value={`${result.entity_type}-${result.entity_id}-${result.title}`}
                      onSelect={() => handleSelect(result)}
                      className="cursor-pointer"
                      data-test="global-search-result"
                      data-entity-type={result.entity_type}
                      data-entity-id={result.entity_id}
                    >
                      <Icon className={`mr-2 h-4 w-4 ${colorClass}`} />
                      <div className="flex flex-1 items-center gap-2 overflow-hidden">
                        <span className="truncate font-medium">{result.title}</span>
                        {result.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">{result.subtitle}</span>
                        )}
                      </div>
                      {result.tenant_slug !== orgSlug && (
                        <Badge variant="outline" className="ml-2 shrink-0 text-[10px]">
                          {result.tenant_slug}
                        </Badge>
                      )}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )
          })}

        {/* Footer */}
        {!loading && (hasQuery || hasRecents) && (
          <div className="border-t px-3 py-2 text-[11px] text-muted-foreground/70">
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">Esc</kbd>{" "}
            fermer{" "}
            <span className="mx-1">·</span>
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">↑↓</kbd>{" "}
            naviguer{" "}
            <span className="mx-1">·</span>
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">↵</kbd>{" "}
            ouvrir{" "}
            <span className="mx-1">·</span>
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">⌘K</kbd>{" "}
            rechercher
          </div>
        )}

        {/* Execution time */}
        {!loading && response && response.total > 0 && (
          <div className="border-t px-3 py-1 text-right text-[10px] text-muted-foreground/50">
            {response.total} résultat{response.total > 1 ? "s" : ""} en {response.execution_ms}ms
          </div>
        )}
      </CommandList>
    </CommandDialog>
  )
}
